import {format} from 'date-fns'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {
  ActionTypeFilter,
  DataObjectId,
  idFromUserEvent,
  ObjectType,
  stickersContainerId,
  StorageDepartmentHierarchy,
  UserEvent
} from 'yougile-model'
import {Sync} from '@client/sync'
import {LinkButton} from '@components/common/button/button'
import {noPropagate, useLoadedLazyTaskEffect} from '@containers/hooks'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {Tile} from '../common/tile/tile'
import {ActionIcon} from '../utils/action-icons/action-icons'
import {getActionName} from '../utils/action-name'
import {ObjectName} from '../utils/object-name'
import {click, getHint, getProjectTitle} from '../utils/utils'
import ChatGototaskSVG from './chat-gototask.svg'
import {FilesPreview} from './files-preview'
import RestoreSVG from './restore.svg'
import {StatsTimelineRowUsername} from './username'

interface Props {
  user: UserProxy
  time: number
  event: UserEvent
  hierarchy: StorageDepartmentHierarchy
  restore: (objId: DataObjectId, objType: ObjectType) => void
  hasError: boolean
}

const TwTime = tw.div`
  flex
  mr-12
  leading-6
  text-11
  text-secondary-old
`

const TwActionClick = tw.div`
  flex
  self-end
  items-center
  outline-none
  cursor-pointer
  text-action-default
  text-12
  hover:underline
`

const TwContent = tw.div<{$event: UserEvent}>`
  ${({$event}) => ($event.type === 'delete' ? 'text-error-old' : '')}
  flex
  w-full
  max-w-380
  overflow-hidden
  text-14
  flex-wrap
  break-words
  min-w-0
`

const TwLeftSide = tw.div`
  flex
  flex-col
  overflow-hidden
  break-words
  max-w-380
  text-ellipsis
`

const TwLeftSideHint = tw.div`
  mr-16
  overflow-hidden
  break-words
  text-11
  text-third-old
  text-ellipsis
`

const TwLeftSideProjectTitle = tw.div`
  mr-16
  overflow-hidden
  font-semibold
  break-words
  text-11
  text-ellipsis
`

const TwRightSide = tw.div`
  flex
  overflow-hidden
  break-words
  min-w-160
  max-w-160
  text-ellipsis
`

const typesRelativeToProjectAndCompany: ActionTypeFilter[] = [
  'add-user-to-project',
  'remove-user-from-project',
  'change-project-roles',
  'change-project-permissions',
  'add-user-to-company',
  'remove-user-from-company'
]

// eslint-disable-next-line max-lines-per-function
export const StatisticsTile = (props: Props) => {
  const {user, event, time, restore, hasError, hierarchy} = props

  const box = Sync.getCurrentSandbox()
  const obj = box.getHub(idFromUserEvent(event.obj))
  if (!obj) {
    return null
  }
  useLoadedLazyTaskEffect(obj, 'statistics-tile')
  const objType = box.getType(obj)
  const clickRestore = (e: React.MouseEvent) => {
    noPropagate(e)
    restore(obj?.id, objType)
  }
  if (hasError) {
    return null
  }

  return (
    <Tile>
      <Tile.Header>
        <TwTime>{format(time, 'HH:mm')}</TwTime>
      </Tile.Header>
      <Tile.Header>
        <div className="flex">
          <ActionIcon event={event} />
        </div>
      </Tile.Header>
      <Tile.Meta>
        <div
          className={`flex justify-between min-w-0 ${
            !typesRelativeToProjectAndCompany.includes(event.type) && 'mb-14'
          }`}
        >
          <div className="flex items-center min-w-0 leading-6">
            <TwContent $event={event}>
              <div
                className={`flex min-w-0 ${
                  event.type === 'file' ? 'flex-col' : ''
                }`}
              >
                <div className="flex flex-wrap min-w-0">
                  <div className="mr-4 overflow-hidden text-ellipsis">
                    {getActionName({
                      event,
                      objType: objType,
                      isSticker: obj.location === stickersContainerId,
                      obj
                    })}
                  </div>
                  <ObjectName event={event} obj={obj} />
                </div>
                <FilesPreview event={event} />
              </div>
            </TwContent>
          </div>
          <div className="ml-6 text-12 min-w-160 max-w-160">
            <StatsTimelineRowUsername user={user} />
          </div>
        </div>
        <div className="flex justify-between min-w-0">
          <TwLeftSide>
            {objType === 'task' && !obj.deleted ? (
              <LinkButton $extraSmall onClick={click(event)}>
                {getProjectTitle(event)}
              </LinkButton>
            ) : (
              <TwLeftSideProjectTitle>
                {!typesRelativeToProjectAndCompany.includes(event.type) &&
                  getProjectTitle(event)}
              </TwLeftSideProjectTitle>
            )}
            <TwLeftSideHint>{getHint({event, obj}, hierarchy)}</TwLeftSideHint>
          </TwLeftSide>
          <TwRightSide>
            {event.type === 'delete' && (
              <>
                {obj.deleted && (
                  <TwActionClick onClick={clickRestore}>
                    <span className="flex self-end text-obscure-old">
                      <RestoreSVG />
                    </span>{' '}
                    {Lang.findPartsMsg(
                      'statsTimeline',
                      `restore${Util.capitalize(objType)}`
                    )}
                  </TwActionClick>
                )}
              </>
            )}
            {objType === 'task' && !obj.deleted && (
              <TwActionClick onClick={click(event)}>
                <span className="flex self-end text-obscure-old">
                  <ChatGototaskSVG />
                </span>{' '}
                {Lang.msg.statsTimeline.goToTask}
              </TwActionClick>
            )}
          </TwRightSide>
        </div>
      </Tile.Meta>
    </Tile>
  )
}
